import type { App, AppTrigger, AppWithRelations } from "~/models/App";
import type { FieldDefinition } from "~/models/Automation";
import type { ApiResponse } from "~/types/ApiResponse";
export const useAppStore = defineStore("apps", () => {
    const api = useApi();

    const app = ref<AppWithRelations>();
    const extra = ref<any>({});
    const saving = ref<boolean>(false);
    const editing = ref<any>();
    const trigger = ref<AppTrigger>();
    const updatingStatus = ref<string | false>(false);

    watch(editing, (to) => {
        if (to === undefined) {
            trigger.value = undefined;
        }
    });

    async function find(uuid: string) {
        return new Promise<ApiResponse<AppWithRelations>>((resolve, reject) => {
            api.apps
                .findRequest(uuid)
                .then((data) => {
                    app.value = data.data;
                    extra.value = data.extra;
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    async function findTrigger(uuid: string) {
        return new Promise<ApiResponse<AppWithRelations>>((resolve, reject) => {
            if (app.value === undefined) {
                reject(new Error("app is undefined"));
            } else {
                const { data, error, waitTillDone } = api.apps
                    .triggers(app.value?.uuid)
                    .find(uuid);

                waitTillDone(() => {
                    if (error.value) {
                        reject(error.value);
                    } else {
                        app.value = data.value.data;
                        resolve(data.value);
                    }
                });
            }
        });
    }

    async function update(data: Partial<App>): Promise<boolean> {
        if (!app.value?.uuid) {
            return false;
        }

        try {
            saving.value = true;
            const response = await api.apps.update(app.value.uuid, data);

            app.value = response.data;

            return true;
        } catch {
            return false;
        } finally {
            saving.value = false;
        }
    }

    async function createTrigger(name: string) {
        if (!app.value?.uuid) {
            return false;
        }

        const { data } = await api.apps.triggers(app.value.uuid).create({
            name,
        });

        editing.value = "trigger";
        trigger.value = data;

        app.value = {
            ...app.value,
            triggers: [...app.value.triggers, data],
        };
    }

    async function updateTrigger(
        data: Partial<AppTrigger>,
        sync = true,
    ): Promise<boolean> {
        if (!app.value?.uuid || !trigger.value?.uuid) {
            return false;
        }

        try {
            saving.value = true;
            const response = await api.apps
                .triggers(app.value.uuid)
                .update(trigger.value.uuid, data);

            if (sync) {
                trigger.value = response.data;
            }

            app.value.updated_at =
                response?.data?.updated_at ?? app.value?.updated_at;

            app.value.triggers = app.value?.triggers.map((trig) => {
                if (trig.uuid === trigger.value.uuid) {
                    return trigger.value;
                }

                return trig;
            });

            return true;
        } catch {
            return false;
        } finally {
            saving.value = false;
        }
    }

    function createFieldDefinition(
        field: Partial<FieldDefinition> = {},
        index?: number,
    ) {
        if (!app.value?.uuid || !trigger.value?.uuid) {
            return false;
        }

        field = {
            label: "",
            attribute: "",
            type: "",
            sub_type: "",
            object: "",
            app: "custom",
            default_value: null,
            data_key: "",
            key: "",
            ...field,
        };

        const fields = [...(trigger.value.object_definition ?? [])];

        if (index !== undefined) {
            fields.splice(index, 0, field);
        } else {
            fields.push(field);
        }

        trigger.value = {
            ...trigger.value,
            object_definition: fields,
        };
    }

    function removeFieldDefinition(index: number) {
        const fields = [...(trigger.value.object_definition ?? [])];
        fields.splice(index, 1);

        trigger.value = {
            ...trigger.value,
            object_definition: fields,
        };
    }

    async function toggleTriggerStatus(
        uuid: string,
        status: AppTrigger["status"],
        silent: boolean = false,
    ) {
        if (!app.value?.uuid) {
            return false;
        }

        updatingStatus.value = uuid;

        const { data } = await api.apps
            .triggers(app.value.uuid)
            .update(uuid, {
                status,
            })
            .finally(() => {
                updatingStatus.value = false;
            });

        app.value = {
            ...app.value,
            triggers: (app.value?.triggers ?? []).map((trigger) => {
                return trigger.uuid === uuid ? data : trigger;
            }),
        };

        if (trigger.value?.uuid === uuid) {
            trigger.value = data;
        }

        if (!silent) {
            useToast().add({
                title:
                    data.status === "active"
                        ? "Trigger Activated"
                        : "Trigger Paused",
                actions: [
                    {
                        label: "Undo",
                        click: () =>
                            toggleTriggerStatus(
                                uuid,
                                data.status === "active" ? "paused" : "active",
                                true,
                            ),
                    },
                ],
            });
        }
    }

    return {
        app,
        extra,
        trigger,
        editing,
        saving,
        find,
        update,
        createFieldDefinition,
        removeFieldDefinition,
        toggleTriggerStatus,
        updatingStatus,
        findTrigger,
        createTrigger,
        updateTrigger,
    };
});
